import { Button, ButtonColor, ButtonVariant, Checkbox, useOutsideClick } from "@swivl-mono/ui";
import { HTMLAttributes, type ReactNode, memo, useState } from "react";
import { AiOutlineFilter } from "react-icons/ai";

import { filterClasses } from "../../../../Helpers/FilterClasses";
import useAutoFocus from "../../../../Hooks/useAutoFocus";
import useKeyPressAction from "../../../../Hooks/useKeyPressAction";

export interface FilterOption {
  id: string;
  label: string;
  filters: { value: string; label: string; disabled: boolean; checked: boolean }[];
}

interface FilterDropdownProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  filterOptions: FilterOption[];
  id?: string;
  position?: "left" | "right";
  resetFilters: () => void;
  saveFilters: (filter: { optionId: string; value: string; checked: boolean }) => void;
  variant?: ButtonVariant;
}

const BaseFilterDropdown = ({ children, filterOptions, id, position = "left", resetFilters, saveFilters, variant = ButtonVariant.DASHED, ...rest }: FilterDropdownProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const menuRef = useOutsideClick(() => setIsDropdownOpen(false));
  useAutoFocus(menuRef, [isDropdownOpen]);

  useKeyPressAction({
    callback: () => setIsDropdownOpen(false),
    key: "Escape",
    targetEl: menuRef.current,
  });

  const handleToggleDropdown = () => {
    setIsDropdownOpen((prevState) => !prevState);
  };

  const activeFiltersTotal = filterOptions.reduce((acc, curr) => {
    const groupNumber = curr.filters.reduce((acc, curr) => {
      if (curr.checked) acc++;
      return acc;
    }, 0);
    return groupNumber + acc;
  }, 0);

  return (
    <div
      aria-expanded={isDropdownOpen}
      className="flex relative" ref={menuRef}
      id={id}
      {...rest}
    >
      <Button
        aria-controls={id}
        aria-expanded={isDropdownOpen}
        onClick={handleToggleDropdown}
        type="button"
        variant={variant}
      >
        {variant === ButtonVariant.GHOST ? (
          <span className={"hover:text-orange-500 flex items-center gap-1 text-black"}>
            <AiOutlineFilter />
            Filter {`(${activeFiltersTotal})`}
          </span>
        ) : "+ Add"}
      </Button>
      <div
        className={filterClasses(
          "flex flex-col w-80 rounded-md max-h-96 bg-white shadow-lg items-center overflow-y-auto [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-grey-400 [&::-webkit-scrollbar-thumb]:rounded-md px-2 transition-all duration-1000 absolute gap-2 top-9 right-0 z-50",
          position === "left" ? "left-0" : "right-0",
          isDropdownOpen ? "h-fit opacity-100" : "h-0 hidden opacity-0",
        )}
      >
        {filterOptions?.map((option) => (
          <div className="px-2 w-full flex flex-col gap-2" key={option.id}>
            <span className="py-2 text-grey-600">{option.label}</span>
            {option.filters.map((filter) => (
              <div className="flex items-center space-x-3" key={filter.value}>
                <Checkbox
                  checked={filter.checked}
                  disabled={filter.disabled}
                  onCheckedChange={(checked) =>
                    saveFilters({
                      optionId: option.id,
                      value: String(filter.value),
                      checked: Boolean(checked),
                    })
                  }
                  name={String(filter.value)}
                  label={option.id === "price" ? `$${filter.label}` : filter.label}
                />
              </div>
            ))}
          </div>
        ))}

        <div className="flex sticky bottom-0 bg-white items-center border-t border-grey-400 py-3.5 justify-between w-full px-2">
          <span>{activeFiltersTotal} Selected</span>
          <div className="flex space-x-2">
            <Button onClick={resetFilters} variant={ButtonVariant.GHOST}>Clear</Button>
            <Button onClick={() => setIsDropdownOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FilterDropdown = memo(BaseFilterDropdown);
